import { useEffect } from "react";
import { YStack } from "tamagui";

import {
  useFetchMedicationHistory,
  useGetMedicationHistoryStatus,
} from "@/api/generated";
import { FetchRxHistoryWorkflowStatusStatus } from "@/api/generated/models";
import { LoadingSpinner, Text } from "@/components";

export const RxHistoryFetch = ({ onContinue }: { onContinue: () => void }) => {
  const { mutate: fetchMedications, isSuccess: fetchStarted } =
    useFetchMedicationHistory({
      mutation: { meta: { suppressErrorToast: true } },
    });

  useEffect(() => {
    fetchMedications(undefined, {
      onError: () => onContinue(),
    });
  }, [fetchMedications, onContinue]);

  const { data: rxHistoryStatus, isError: statusFetchFailed } =
    useGetMedicationHistoryStatus({
      query: {
        // Wait until the workflow start has been acknowledged; otherwise the GET
        // returns 404 (no workflow handle yet) and triggers the global error path.
        enabled: fetchStarted,
        refetchInterval: (query) =>
          query.state.data?.status ===
          FetchRxHistoryWorkflowStatusStatus.in_progress
            ? 5000
            : false,
        // Don't carry status across mounts: a cached `completed` from a prior
        // fetch would advance the user before the new workflow even starts.
        staleTime: 0,
        gcTime: 0,
        meta: { suppressErrorToast: true },
      },
    });

  useEffect(() => {
    const status = rxHistoryStatus?.status;
    if (
      status === FetchRxHistoryWorkflowStatusStatus.completed ||
      status === FetchRxHistoryWorkflowStatusStatus.failed ||
      statusFetchFailed
    ) {
      onContinue();
    }
  }, [rxHistoryStatus?.status, statusFetchFailed, onContinue]);

  return (
    <YStack
      flex={1}
      justifyContent="center"
      alignItems="center"
      gap="$100"
      paddingHorizontal="$200"
      paddingBottom="$200"
    >
      <LoadingSpinner size="$1000" />
      <Text semibold>Looking for prescriptions</Text>
      <Text color="$textSecondary">This can take up to 3 minutes...</Text>
    </YStack>
  );
};
